import { Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import InputField from "components/Input/InputField";
import { DocumentSlot } from "./WizardParts";
import { AU_STATES, PARTNER_FIELD_MAX_LENGTH } from "const/referralPartnerFields";
import { DEFAULT_TERMS, DOC_SLOTS, ENTITY_TYPES, digitsOnly } from "./wizardConfig";

const grid = "grid grid-cols-1 sm:grid-cols-2 gap-4";

/** A state already on file in another spelling stays selectable so a locked value still shows. */
const stateOptions = (current) =>
  current && !AU_STATES.includes(current) ? [current, ...AU_STATES] : AU_STATES;

/**
 * The inputs for one wizard step. A locked field shows the value the office already holds, disabled,
 * with the reason in place of the helper text.
 */
const WizardStepFields = ({
  stepId,
  form,
  errors,
  patch,
  patchField,
  lockedHint,
  requirePassword,
  documentOnFile,
  uploads,
  selectDocument,
  terms,
  orgLabel,
}) => {
  const field = (name, helper) => {
    const hint = lockedHint(name);
    return {
      name,
      value: form[name],
      disabled: Boolean(hint),
      error: !hint && Boolean(errors[name]),
      helperText: hint || errors[name] || helper,
      maxLength: PARTNER_FIELD_MAX_LENGTH[name],
    };
  };
  const selectField = (name) => {
    const { maxLength, ...rest } = field(name);
    return rest;
  };
  const digitsInto = (name, max) => (event) =>
    patchField(name, digitsOnly(event.target.value).slice(0, max));

  if (stepId === "profile") {
    return (
      <div className={grid}>
        <InputField label="Full name" required onChange={patch} autoFocus {...field("fullName")} />
        <InputField label="Trading name" placeholder="Optional" onChange={patch} {...field("tradingName")} />
        <InputField label="Email" type="email" required onChange={patch} {...field("email")} />
        <InputField label="Phone" type="tel" required onChange={patch} {...field("phone")} />
      </div>
    );
  }

  if (stepId === "business") {
    return (
      <div className={grid}>
        <TextField select size="small" label="Entity type" required onChange={patch} {...selectField("entityType")}>
          {ENTITY_TYPES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="GST registered" onChange={patch} {...selectField("gstRegistered")}>
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </TextField>
        <InputField
          label="ABN"
          placeholder="Optional · 11 digits"
          {...field("abn", "Optional: add if you have one")}
          onChange={digitsInto("abn", 11)}
        />
        <InputField
          label="TFN"
          placeholder="Optional · 8 or 9 digits"
          {...field("tfn", "Optional: stored securely if provided")}
          onChange={digitsInto("tfn", 9)}
        />
      </div>
    );
  }

  if (stepId === "address") {
    return (
      <div className="space-y-4">
        <InputField label="Address line 1" required onChange={patch} autoFocus {...field("addressLine1")} />
        <InputField label="Address line 2" placeholder="Optional" onChange={patch} {...field("addressLine2")} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <InputField label="Suburb" required onChange={patch} {...field("suburb")} />
          <TextField select size="small" label="State" required onChange={patch} {...selectField("state")}>
            {stateOptions(form.state).map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
          <InputField
            label="Postcode"
            required
            placeholder="4 digits"
            {...field("postcode")}
            onChange={digitsInto("postcode", 4)}
          />
        </div>
      </div>
    );
  }

  if (stepId === "bank") {
    return (
      <div className={grid}>
        <InputField label="Account name" required onChange={patch} autoFocus {...field("bankAccountName")} />
        <InputField label="BSB" required placeholder="6 digits" {...field("bsb")} onChange={digitsInto("bsb", 6)} />
        <InputField
          label="Account number"
          required
          {...field("accountNumber")}
          onChange={digitsInto("accountNumber", 12)}
        />
      </div>
    );
  }

  if (stepId === "documents") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-500 m-0">
          Upload clear scans or photos. A photo ID is required for verification.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DOC_SLOTS.map((slot) => (
            <DocumentSlot
              key={slot.key}
              slot={slot}
              file={form[slot.key]}
              onFile={documentOnFile(slot.key)}
              error={errors[slot.key]}
              uploadState={uploads[slot.key]}
              onSelect={(file) => selectDocument(slot.key, file)}
              onClear={() => patchField(slot.key, null)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {requirePassword ? (
        <div className={grid}>
          <InputField
            label="Password"
            name="password"
            type="password"
            required
            value={form.password}
            onChange={patch}
            error={Boolean(errors.password)}
            helperText={errors.password || "At least 10 characters, mixing letters with numbers or symbols"}
            autoFocus
          />
          <InputField
            label="Confirm password"
            name="confirmPassword"
            type="password"
            required
            value={form.confirmPassword}
            onChange={patch}
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
          />
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold text-slate-700 m-0 mb-2">
          Terms & conditions{terms?.version ? ` · v${terms.version}` : ""}
        </p>
        <div className="max-h-40 overflow-y-auto rounded-lg bg-white border border-slate-200 p-3 text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">
          {terms?.html ? (
            // Authored by the office and served by the backend, not user input.
            <div dangerouslySetInnerHTML={{ __html: terms.html }} />
          ) : (
            DEFAULT_TERMS
          )}
        </div>
        <FormControlLabel
          sx={{ mt: 1, alignItems: "flex-start", ml: 0 }}
          control={
            <Checkbox
              name="acceptTerms"
              checked={form.acceptTerms}
              onChange={patch}
              color="primary"
              sx={{ pt: 0.25 }}
            />
          }
          label={
            <span className="text-sm text-slate-800">
              I agree to {orgLabel}&apos;s referral partner terms and conditions
              <sup className="text-red-500"> *</sup>
            </span>
          }
        />
        {errors.acceptTerms ? <p className="text-xs text-red-500 m-0">{errors.acceptTerms}</p> : null}
      </div>
    </div>
  );
};

export default WizardStepFields;
